import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import image9 from "../assets/img9.avif";
import image10 from "../assets/img10.avif";
import image11 from "../assets/img11.avif";
import image12 from "../assets/img12.avif";

const stepImages = [image9, image10, image11, image12];

const HowItWorking = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const controls = useAnimation();
  const ctaRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(true);

  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: false,
  });

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    } else {
      controls.start("hidden");
    }
  }, [controls, inView]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (ctaRef.current && showScrollHint) {
        ctaRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        setShowScrollHint(false);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [showScrollHint]);

  const handleSignIn = () => {
    navigate("/login");
  };

  const handleViewMatches = () => {
    navigate("/login");
  };

  const stepVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const imageHoverVariants = {
    hover: {
      scale: 1.03,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
  };

  const buttonHoverVariants = {
    hover: {
      scale: 1.05,
      boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.2)",
      transition: {
        duration: 0.2,
      },
    },
    tap: {
      scale: 0.98,
    },
  };

  return (
    <div className="bg-gradient-to-b from-gray-50 to-gray-100 min-h-screen flex flex-col overflow-hidden">
      <div className="flex-grow py-12 px-4 sm:px-6 lg:px-8 text-gray-800">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate("/")}
            className="absolute top-4 left-4 z-20 flex items-center text-[#e67e22] hover:text-[#d35400] transition-colors duration-200 sm:top-3 sm:left-3 md:top-4 md:left-4 lg:top-5 lg:left-5 xl:top-6 xl:left-6"
          >
            <ArrowLeft className="h-5 w-5 sm:h-4 sm:w-4 md:h-5 md:w-5 lg:h-6 lg:w-6 xl:h-7 xl:w-7" />
          </button>
          <motion.div
            className="text-center mb-12 md:mb-16"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#d35400] mb-4">
              {t("howItWorks")}
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              {t("subtitle")}
            </p>
          </motion.div>

          <motion.div
            className="space-y-16 md:space-y-20"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            ref={ref}
          >
            {[0, 1, 2, 3].map((index) => (
              <motion.div
                key={index}
                className={`flex flex-col ${index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"} items-center gap-6 md:gap-10 lg:gap-12`}
                variants={stepVariants}
              >
                <motion.div
                  className="relative w-full md:w-1/2 group"
                  whileHover="hover"
                  variants={imageHoverVariants}
                >
                  <img
                    src={stepImages[index]}
                    alt={t(`steps.${index}.title`)}
                    className="w-full rounded-xl md:rounded-2xl shadow-lg md:shadow-xl object-cover h-60 sm:h-72 md:h-80 lg:h-96 transition-all duration-500 group-hover:shadow-2xl group-hover:brightness-90"
                  />
                  <motion.div
                    className="absolute -top-3 -left-3 sm:-top-4 sm:-left-4 bg-[#e67e22] text-white text-2xl sm:text-3xl w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center rounded-full shadow-lg"
                    whileHover={{ scale: 1.1, rotate: 10 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    {t(`steps.${index}.icon`)}
                  </motion.div>
                </motion.div>

                <div className="w-full md:w-1/2 text-center md:text-left">
                  <div className="max-w-md mx-auto md:mx-0">
                    <motion.h3
                      className="text-xl sm:text-2xl md:text-3xl font-bold text-[#e67e22] mb-3 sm:mb-4 flex items-center justify-center md:justify-start"
                      whileHover={{ x: 5 }}
                    >
                      <span className="mr-2 sm:mr-3">
                        {t(`steps.${index}.icon`)}
                      </span>
                      {t(`steps.${index}.title`)}
                    </motion.h3>
                    <motion.p
                      className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.4 }}
                    >
                      {t(`steps.${index}.description`)}
                    </motion.p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="mt-16 md:mt-20 text-center"
            ref={ctaRef}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.h3
              className="text-xl sm:text-2xl md:text-3xl font-bold text-[#d35400] mb-6"
              whileHover={{ scale: 1.02 }}
            >
              {t("ctaTitle")}
            </motion.h3>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
              <motion.button
                onClick={handleViewMatches}
                className="px-6 py-3 bg-gradient-to-r from-[#e67e22] to-[#d35400] text-white rounded-lg font-medium relative overflow-hidden"
                variants={buttonHoverVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <span className="relative z-10">{t("viewMatches")}</span>
                <motion.span
                  className="absolute inset-0 bg-gradient-to-r from-[#d35400] to-[#e67e22] opacity-0"
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.button>
              <motion.button
                onClick={handleSignIn}
                className="px-6 py-3 border-2 border-[#e67e22] text-[#e67e22] rounded-lg font-medium relative overflow-hidden"
                variants={buttonHoverVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <span className="relative z-10">{t("signIn")}</span>
                <motion.span
                  className="absolute inset-0 bg-[#e67e22] opacity-0"
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>

      <AnimatePresence>
        {showScrollHint && (
          <motion.div
            className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-[#e67e22] text-white px-4 py-2 rounded-full shadow-lg flex items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <span className="mr-2">{t("scrollDown")}</span>
            <motion.div
              animate={{ y: [0, 5, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
            >
              ↓
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HowItWorking;
